import { useTranslation } from "react-i18next";
import { Loader2, Power, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import type { AssignmentUpdateAction } from "../staff/types";

interface StaffBulkActionsProps {
  readonly selectedCount: number;
  readonly isPending: boolean;
  readonly onRun: (action: Exclude<AssignmentUpdateAction, "remove">) => void;
  readonly onRequestRemove: () => void;
}

/** Bulk deactivate / reactivate / remove over the ticked assignments (ADMIN only). */
export function StaffBulkActions({
  selectedCount,
  isPending,
  onRun,
  onRequestRemove,
}: StaffBulkActionsProps) {
  const { t } = useTranslation();
  const disabled = selectedCount === 0 || isPending;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground" aria-live="polite">
        {t("users.staff.selectedCount", { count: selectedCount })}
      </span>
      <Button
        data-testid="staff-bulk-deactivate"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onRun("deactivate")}
      >
        <Power className="mr-2 h-4 w-4" aria-hidden="true" />
        {t("users.staff.bulk.deactivate")}
      </Button>
      <Button
        data-testid="staff-bulk-reactivate"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onRun("reactivate")}
      >
        <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
        {t("users.staff.bulk.reactivate")}
      </Button>
      <Button
        data-testid="staff-bulk-remove"
        variant="destructive"
        size="sm"
        disabled={disabled}
        onClick={onRequestRemove}
      >
        <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
        {t("users.staff.bulk.remove")}
      </Button>
      {isPending && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );
}
